import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

/** Shown once, the first time an invited member signs in. */
export default function ForcePasswordChange({ user, onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("The two new passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      const { data } = await api.get("/auth/me");
      onDone(data);
      toast.success("Password set — welcome aboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-5">
      <form
        onSubmit={submit}
        data-testid="force-password-form"
        className="w-full max-w-md border border-border bg-card/40 p-8"
        style={{ borderRadius: "var(--radius)" }}
      >
        <ShieldCheck className="h-8 w-8 text-primary" />
        <h1 className="mt-4 font-head text-3xl">Choose your password</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          You are signed in as {user.email} with a temporary password. Pick your own to continue.
        </p>
        <div className="mt-6 space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">Temporary password</Label>
            <Input
              className="mt-1.5"
              type="password"
              required
              value={current}
              data-testid="force-current-input"
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">New password (at least 8 characters)</Label>
            <Input
              className="mt-1.5"
              type="password"
              required
              minLength={8}
              value={next}
              data-testid="force-new-input"
              onChange={(e) => setNext(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Type it again</Label>
            <Input
              className="mt-1.5"
              type="password"
              required
              value={confirm}
              data-testid="force-confirm-input"
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <button type="submit" disabled={busy} data-testid="force-submit-btn" className="btn-gold w-full disabled:opacity-60">
            {busy ? "Saving…" : "Save and continue"}
          </button>
        </div>
      </form>
    </div>
  );
}
